import { type Mandator } from '@einwert/valuation-api-client'
import type { Contact as ContactSchema } from '@einwert/valuation-api-client'
import type { AssetUsageType } from '@einwert/valuation-api-client'
import type { CreateNewOrder } from 'ClientPortal/src/pages/Orders/CreateOrder/types'

export type OrderAction =
    | AddContactAction
    | AddMandatorAction
    | RemoveContactAction
    | RemoveMandatorAction
    | ReplaceAssetUsageTypesAction
    | UpdateContactAction
    | UpdateAssetAddressAction
    | UpdateOrderAction
    | UpdateAssetIdAction
    | UpdateAssetStateAction
    | UpdateMandatorAction
    | UpdateCostCenterAction
    | UpdateAssignedCustomerUsersAction
    | UpdateCustomerAssetReferenceAction

export type CreateNewOrderWithEmptyReason = Omit<CreateNewOrder, 'valuation_reason'> & {
    valuation_reason: '' | CreateNewOrder['valuation_reason']
}

export function orderReducer(order: CreateNewOrderWithEmptyReason, action: OrderAction): CreateNewOrderWithEmptyReason {
    switch (action.type) {
        case 'addContact':
            return {
                ...order,
                contacts: [action.payload, ...order.contacts],
            }
        case 'removeContact':
            return {
                ...order,
                contacts: order.contacts.filter((_, index) => index !== action.index),
            }
        case 'updateContact':
            return {
                ...order,
                contacts: order.contacts.map((contact, index) => (index === action.index ? action.payload : contact)),
            }
        case 'updateAssetAddress':
            return {
                ...order,
                address: action.payload,
                asset_uuid: undefined,
            }
        case 'updateCustomerAssetReference':
            return {
                ...order,
                customer_asset_reference: action.payload,
            }
        case 'updateAssetId':
            return {
                ...order,
                asset_uuid: action.payload.assetId,
                //remove once backend's address field is optional
                address: action.payload.address,
            }
        case 'updateAssetState':
            return {
                ...order,
                asset_state: action.payload,
            }
        case 'updateOrder':
            return {
                ...order,
                ...action.payload,
            }
        case 'addMandator':
            return {
                ...order,
                mandators: [...order.mandators, action.payload],
            }
        case 'removeMandator':
            return {
                ...order,
                mandators: order.mandators.filter((_, index) => index !== action.index),
            }
        case 'updateMandator':
            return {
                ...order,
                mandators: order.mandators.map((mandator, index) => (index === action.index ? action.payload : mandator)),
            }
        case 'updateCostCenter':
            return {
                ...order,
                cost_center_uuid: action.payload,
            }
        case 'updateAssignedCustomerUsers':
            return {
                ...order,
                assigned_customer_users: action.payload,
            }
        case 'replaceAssetUsageTypes':
            return {
                ...order,
                asset_usage_types: action.payload,
            }
        default:
            throw new UnsupportedActionError(action)
    }
}

class UnsupportedActionError extends Error {
    constructor(value: never) {
        super(`Action type doesn't exist ${value}`)
    }
}

type UpdateCustomerAssetReferenceAction = {
    type: 'updateCustomerAssetReference'
    payload: string | undefined
}

type AddContactAction = {
    type: 'addContact'
    payload: ContactSchema
}

type RemoveContactAction = {
    type: 'removeContact'
    index: number
}

type UpdateContactAction = {
    type: 'updateContact'
    index: number
    payload: ContactSchema
}

type UpdateAssetAddressAction = {
    type: 'updateAssetAddress'
    payload: CreateNewOrder['address']
}

type UpdateOrderAction = {
    type: 'updateOrder'
    payload: CreateNewOrderWithEmptyReason
}

type UpdateAssetIdAction = {
    type: 'updateAssetId'
    payload: {
        assetId: CreateNewOrder['asset_uuid']
        ////remove once backend's address field is optional
        address: CreateNewOrder['address']
    }
}

type UpdateAssetStateAction = {
    type: 'updateAssetState'
    payload: CreateNewOrder['asset_state']
}

type AddMandatorAction = {
    type: 'addMandator'
    payload: Mandator
}

type UpdateMandatorAction = {
    type: 'updateMandator'
    index: number
    payload: Mandator
}

type RemoveMandatorAction = {
    type: 'removeMandator'
    index: number
}

type ReplaceAssetUsageTypesAction = {
    type: 'replaceAssetUsageTypes'
    payload: AssetUsageType[]
}

type UpdateCostCenterAction = {
    type: 'updateCostCenter'
    payload: string | undefined
}

type UpdateAssignedCustomerUsersAction = {
    type: 'updateAssignedCustomerUsers'
    payload: number[]
}
